import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { 
  Car, 
  Users, 
  ClipboardList, 
  AlertTriangle,
  LogOut,
  User as UserIcon,
  TrendingUp,
  TrendingDown,
  Activity,
  Fuel,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Wrench,
  Award,
  BarChart3,
  DollarSign,
  MapPin,
  Bell,
  RefreshCw,
  Wifi,
  WifiOff,
  Zap,
  Shield,
  TrendingUp as TrendingUpIcon,
  Search,
  Upload,
  History,
  Plus,
  X,
  Forklift,
  Truck,
  Clipboard,
  ArrowRight,
  RotateCcw,
  ScanLine,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import type { PermissionModule } from "../config/permissions";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar
} from "recharts";

interface ActiveAssignment {
  _id: string;
  vehiclePlate: string;
  driverName: string;
  driverId: string;
  startDate: string;
  startTime: string;
  purpose?: string;
  currentStep: string;
}

interface DashboardStats {
  drivers: {
    total: number;
    active: number;
    inactive: number;
    byLicenseType: Array<{ _id: string; count: number }>;
    expiringLicenses: Array<any>;
    topDrivers: Array<any>;
  };
  vehicles: {
    total: number;
    available: number;
    inService: number;
    maintenance: number;
    statistics: {
      averageMileage: number | null;
      totalMileage: number;
      minMileage: string;
      maxMileage: string;
    };
    topVehicles: Array<any>;
    byBrand: Array<{ _id: string; count: number }>;
    byOwnership: Array<{ _id: string; count: number }>;
    byFuelType: Array<{ _id: string; count: number }>;
    byType: Array<{ _id: string; count: number }>;
  };
  assignments: {
    active: number;
    completed: number;
    byMonth: Array<{ _id: { year: number; month: number }; count: number }>;
    averageDuration: number;
  };
  damages: {
    total: number;
    byDriver: Array<any>;
    byVehicle: Array<any>;
    trend: Array<any>;
    byType: Array<{ _id: string; count: number }>;
    byFace: Array<{ _id: string; count: number }>;
  };
}

const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  purple: '#8B5CF6',
  cyan: '#06B6D4',
  pink: '#EC4899',
  indigo: '#6366F1'
};

const CHART_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.error, COLORS.purple, COLORS.cyan];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeAssignments, setActiveAssignments] = useState<ActiveAssignment[]>([]);
  
  // Recherche rapide véhicule
  const [searchPlate, setSearchPlate] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{plate: string; brand?: string; model?: string}>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [backendConnected, setBackendConnected] = useState(false);
  
  // État pour le FAB (Floating Action Button)
  const [fabOpen, setFabOpen] = useState(false);
  
  // Récupérer les éléments récents depuis localStorage
  const [recentItems, setRecentItems] = useState<{
    vehicles: Array<{plate: string; brand?: string; model?: string}>;
    drivers: Array<{id: string; name: string}>;
  }>({ vehicles: [], drivers: [] });
  
  useEffect(() => {
    const saved = localStorage.getItem('recentItems');
    if (saved) {
      try {
        setRecentItems(JSON.parse(saved));
      } catch (e) {
        console.error('Erreur lecture recentItems:', e);
      }
    }
  }, []);

  // Fonction de recherche de véhicule par plaque
  const searchVehicle = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data } = await axios.get(`${API_URL}/vehicles?search=${encodeURIComponent(query.toUpperCase())}`);
      const vehicles = Array.isArray(data) ? data : data.vehicles || [];
      const filtered = vehicles
        .filter((v: any) => v.plate?.toUpperCase().includes(query.toUpperCase()))
        .slice(0, 5)
        .map((v: any) => ({
          plate: v.plate,
          brand: v.brand,
          model: v.vehicleModel || v.model,
        }));
      setSearchResults(filtered);
      setShowSearchResults(filtered.length > 0);
    } catch (err) {
      console.error("Erreur recherche véhicule:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchPlate) {
        searchVehicle(searchPlate);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchPlate, searchVehicle]);

  const handleSelectVehicle = (plate: string, action: "history" | "assignment") => {
    setSearchPlate("");
    setShowSearchResults(false);
    if (action === "history") {
      navigate(`/vehicules/historique/${plate}`);
    } else {
      navigate(`/assignments/new?plate=${plate}`);
    }
  };

  const fetchStats = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setIsRefreshing(true);
      setError(null);
      
      // Test de connexion backend
      const healthCheck = await axios.get(`${API_URL.replace('/api', '')}/health`);
      setBackendConnected(healthCheck.data.status === 'OK');
      
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        timeout: 10000
      });
      
      setStats(response.data);
      setLastUpdate(new Date());
      setBackendConnected(true);
      
      // Récupérer les affectations en cours
      try {
        const assignmentsResponse = await axios.get(`${API_URL}/assignments`);
        const allAssignments = assignmentsResponse.data || [];
        const active = allAssignments.filter((a: any) => a.status === 'en_cours').map((a: any) => ({
          _id: a._id,
          vehiclePlate: a.vehiclePlate,
          driverName: `${a.driverFirstName || ''} ${a.driverLastName || ''}`.trim(),
          driverId: a.driverId,
          startDate: a.date,
          startTime: a.time,
          purpose: a.purpose,
          currentStep: a.currentStep
        }));
        setActiveAssignments(active);
      } catch (assignErr) {
        console.error("Erreur chargement affectations actives:", assignErr);
      }
    } catch (err: any) {
      console.error("Erreur lors du chargement des statistiques:", err);
      setBackendConnected(false);
      
      if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
        setError("Impossible de se connecter au serveur. Vérifiez votre connexion.");
      } else if (err.response?.status === 404) {
        setError("Endpoint non trouvé. Vérifiez la configuration du backend.");
      } else {
        setError(`Erreur: ${err.response?.data?.message || err.message || 'Erreur inconnue'}`);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh toutes les 30 secondes
    const interval = setInterval(() => {
      fetchStats(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleRefresh = () => {
    fetchStats(true);
  };

  const vehicleStats = stats?.vehicles?.statistics;

  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-16 h-16">
            <div className="absolute w-16 h-16 border-4 border-blue-200 rounded-full"></div>
            <div className="absolute w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-700 font-medium">Chargement des statistiques...</p>
          <p className="text-sm text-gray-500">Connexion au backend...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-2xl shadow-lg border border-red-200">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-red-100 rounded-full">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Erreur de connexion</h2>
            <p className="text-gray-600 text-center">{error}</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleRefresh}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Réessayer
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for charts
  const damagesByTypeData = stats?.damages.byType.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  const monthlyAssignmentsData = stats?.assignments.byMonth
    .filter(item => item._id.year && item._id.month)
    .slice(-6)
    .map(item => ({
      month: `${String(item._id.month).padStart(2, '0')}/${item._id.year}`,
      affectations: item.count
    })) || [];

  // Si pas de données mensuelles valides, créer des données par défaut
  if (monthlyAssignmentsData.length === 0 && stats?.assignments.completed) {
    const now = new Date();
    monthlyAssignmentsData.push({
      month: `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`,
      affectations: stats.assignments.completed
    });
  }

  const licenseTypeData = stats?.drivers.byLicenseType.map(item => ({
    name: `Permis ${item._id}`,
    value: item.count
  })) || [];

  const vehicleStatusData = [
    { name: "Disponibles", value: stats?.vehicles.available || 0, color: COLORS.success },
    { name: "En service", value: stats?.vehicles.inService || 0, color: COLORS.primary },
    { name: "Maintenance", value: stats?.vehicles.maintenance || 0, color: COLORS.warning }
  ].filter(item => item.value > 0);

  // Calcul des pourcentages pour les tendances
  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Préparer les détails pour chaque carte
  const licenseDetails = stats?.drivers.byLicenseType?.map(l => `${l._id}: ${l.count}`).join(", ") || "";
  const brandDetails = stats?.vehicles.byBrand?.slice(0, 3).map(b => `${b._id}: ${b.count}`).join(", ") || "";
  const ownershipDetails = stats?.vehicles.byOwnership?.map(o => {
    const label = o._id === "propriétaire" ? "Prop." : o._id === "leasing" ? "Leasing" : o._id === "location" ? "Loc." : o._id;
    return `${label}: ${o.count}`;
  }).join(", ") || "";
  const damageTypeDetails = stats?.damages.byType?.slice(0, 3).map(d => `${d._id}: ${d.count}`).join(", ") || "";

  // Labels pour les types de véhicules
  const vehicleTypeLabels: Record<string, string> = {
    vul: "VUL",
    pl_fourgon: "PL Fourgon",
    pl_citerne: "PL Citerne",
    pl_plateau: "PL Plateau",
    tracteur_routier: "Tracteur",
    semi_fourgon: "Semi Fourgon",
    semi_citerne: "Semi Citerne",
    semi_plateau: "Semi Plateau",
    remorque_fourgon: "Rem. Fourgon",
    remorque_citerne: "Rem. Citerne",
    remorque_plateau: "Rem. Plateau",
    vl_autocar: "VL/Autocar",
    // Legacy mappings for backward compatibility
    vl: "VL",
    pl: "PL",
    spl: "SPL",
    bus: "Bus",
    autocar: "Autocar",
    remorque: "Remorque",
    semi_remorque: "Semi-rem.",
    fourgon: "Fourgon",
    camion_benne: "Benne",
    camion_citerne: "Citerne",
    porte_conteneur: "Conteneur",
    frigorifique: "Frigo",
  };

  const vehicleTypeDetails = stats?.vehicles.byType?.slice(0, 4).map(t => 
    `${vehicleTypeLabels[t._id] || t._id}: ${t.count}`
  ).join(", ") || "";

  // Données pour le graphique des types de véhicules
  const vehicleTypeChartData = stats?.vehicles.byType?.map(t => ({
    name: vehicleTypeLabels[t._id] || t._id || "Autre",
    value: t.count,
  })) || [];

  const statCards = [
    {
      title: "Chauffeurs",
      value: stats?.drivers.total || 0,
      subtitle: `${stats?.drivers.active || 0} actifs, ${stats?.drivers.inactive || 0} inactifs`,
      details: licenseDetails ? `Permis: ${licenseDetails}` : null,
      trend: stats?.drivers.total ? "+5%" : "0%",
      trendUp: true,
      icon: Users,
      color: "bg-blue-500",
      lightColor: "bg-blue-50",
      textColor: "text-blue-600",
      link: "/chauffeurs",
      percentage: stats?.drivers.total && stats.vehicles.total 
        ? Math.round((stats.drivers.total / Math.max(stats.vehicles.total, 1)) * 100)
        : 0,
      module: "drivers" as PermissionModule,
    },
    {
      title: "Véhicules",
      value: stats?.vehicles.total || 0,
      subtitle: `${stats?.vehicles.available || 0} dispo, ${stats?.vehicles.inService || 0} en service, ${stats?.vehicles.maintenance || 0} maint.`,
      details: vehicleTypeDetails ? `Types: ${vehicleTypeDetails}` : (brandDetails ? `Marques: ${brandDetails}` : null),
      extraDetails: ownershipDetails ? `${ownershipDetails}` : null,
      trend: stats?.vehicles.total ? "+3%" : "0%",
      trendUp: true,
      icon: Car,
      color: "bg-purple-500",
      lightColor: "bg-purple-50",
      textColor: "text-purple-600",
      link: "/vehicules",
      percentage: stats?.vehicles.total 
        ? Math.round(((stats.vehicles.available || 0) / stats.vehicles.total) * 100)
        : 0,
      module: "vehicles" as PermissionModule,
    },
    {
      title: "Affectations",
      value: stats?.assignments.active || 0,
      subtitle: `${stats?.assignments.completed || 0} terminées ce mois`,
      details: stats?.assignments.averageDuration ? `Durée moy: ${Math.round(stats.assignments.averageDuration)}h` : null,
      trend: stats?.assignments.active ? "+8%" : "0%",
      trendUp: true,
      icon: ClipboardList,
      color: "bg-green-500",
      lightColor: "bg-green-50",
      textColor: "text-green-600",
      link: "/assignments",
      percentage: stats?.assignments.active && stats.assignments.completed
        ? Math.round((stats.assignments.active / (stats.assignments.active + stats.assignments.completed)) * 100)
        : 0,
      module: "assignments" as PermissionModule,
    },
    {
      title: "Sinistres signalés",
      value: stats?.damages.total || 0,
      subtitle: "Total des incidents",
      details: damageTypeDetails ? `Types: ${damageTypeDetails}` : null,
      trend: stats?.damages.total ? "-3%" : "0%",
      trendUp: false,
      icon: AlertTriangle,
      color: "bg-red-500",
      lightColor: "bg-red-50",
      textColor: "text-red-600",
      link: "/amendes-degats",
      percentage: 0,
      module: "damages" as PermissionModule,
    }
  ];

  const quickActions = [
    {
      title: "Nouvelle affectation",
      icon: ClipboardList,
      color: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
      link: "/assignments/new",
      module: "assignments" as PermissionModule,
    },
    {
      title: "Cartes carburant",
      icon: Fuel,
      color: "bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800",
      link: "/cartes-carburant",
      module: "fuelCards" as PermissionModule,
    },
    {
      title: "Historique de la flotte",
      icon: Clock,
      color: "bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800",
      link: "/historique-vehicules",
      module: "vehicles" as PermissionModule,
    },
    {
      title: "Import de données",
      icon: Upload,
      color: "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800",
      link: "/import",
      module: "vehicles" as PermissionModule,
    },
    {
      title: "Bibliothèque véhicules",
      icon: Truck,
      color: "bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800",
      link: "/vehicle-library",
      module: "vehicles" as PermissionModule,
    },
    {
      title: "Équipements manutention",
      icon: Forklift,
      color: "bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800",
      link: "/equipements-manutention",
      module: "vehicles" as PermissionModule,
    },
    ...(user?.isSuperAdmin
      ? [
          {
            title: "Gestion des rôles",
            icon: Shield,
            color: "bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-black",
            link: "/super-admin/roles",
          },
          {
            title: "Gestion des utilisateurs",
            icon: Shield,
            color: "bg-gradient-to-r from-neutral-600 to-neutral-800 hover:from-neutral-700 hover:to-black",
            link: "/super-admin/users",
          },
        ]
      : []),
  ];

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Header simplifié intégré */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-block w-2 h-2 rounded-full ${backendConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <p className="text-sm text-gray-500">
              {backendConnected ? 'Système opérationnel' : 'Déconnecté'} 
              {lastUpdate && ` • Mis à jour ${lastUpdate.toLocaleTimeString('fr-FR')}`}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>
      
      {/* Accès direct Scan Plaque pour managers */}
      {(hasPermission("assignments", "create") || user?.isSuperAdmin) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate("/assignments?quickscan=true")}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 mb-4 sm:mb-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-md hover:from-green-600 hover:to-emerald-700 transition-all"
          >
            <ScanLine className="w-5 h-5" />
            <span className="font-semibold text-sm sm:text-base">Scan Plaque (création rapide)</span>
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">{error}</p>
            <button
              onClick={handleRefresh}
              className="ml-auto text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Widget "Qui a quoi" - Affectations en cours */}
        {(hasPermission("assignments", "read") || user?.isSuperAdmin) && activeAssignments.length > 0 && (
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold">🚗 Qui a quoi</h2>
                  <p className="text-xs sm:text-sm text-blue-100">{activeAssignments.length} véhicule{activeAssignments.length > 1 ? 's' : ''} en service</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/assignments')}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg transition-colors text-xs sm:text-sm font-medium"
              >
                Voir tout
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {activeAssignments.slice(0, 6).map((assignment) => (
                <div
                  key={assignment._id}
                  onClick={() => navigate(`/assignments`)}
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-xl p-4 cursor-pointer transition-all hover:scale-105 border border-white/20"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Car className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{assignment.vehiclePlate}</p>
                      <p className="text-xs text-blue-100">Véhicule</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-blue-200" />
                    <p className="text-sm font-medium truncate">{assignment.driverName || assignment.driverId}</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-blue-100 mt-3 pt-3 border-t border-white/20">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>Depuis {assignment.startTime}</span>
                    </div>
                    <div className="px-2 py-1 bg-green-500/30 rounded-full text-white font-medium">
                      En cours
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {activeAssignments.length > 6 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/assignments')}
                  className="text-sm text-blue-100 hover:text-white transition-colors underline"
                >
                  + {activeAssignments.length - 6} autre{activeAssignments.length - 6 > 1 ? 's' : ''} affectation{activeAssignments.length - 6 > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Résumé rapide disponibilité */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Véhicules dispo</h3>
              </div>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-green-600">{stats?.vehicles.available || 0}</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">sur {stats?.vehicles.total || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Chauffeurs libres</h3>
              </div>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-blue-600">{(stats?.drivers.active || 0) - activeAssignments.length}</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">sur {stats?.drivers.active || 0}</p>
          </div>
          
          <div className="col-span-2 md:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Missions en cours</h3>
              </div>
            </div>
            <p className="text-2xl sm:text-4xl font-bold text-orange-600">{activeAssignments.length}</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">active(s)</p>
          </div>
        </div>

        {/* Stats Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            const TrendIcon = card.trendUp ? TrendingUp : TrendingDown;
            
            if (card.module && !hasPermission(card.module as PermissionModule, "read") && !user?.isSuperAdmin) {
              return null;
            }

            return (
              <div
                key={card.title}
                onClick={() => navigate(card.link)}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                style={{
                  animation: `slideUp 0.5s ease-out ${index * 0.1}s both`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${card.lightColor} group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-6 h-6 ${card.textColor}`} />
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${card.trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'} text-xs font-semibold`}>
                      <TrendIcon className="w-3 h-3" />
                      {card.trend}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      {card.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                      {card.value}
                    </p>
                    <p className="text-sm text-gray-500 mb-1">{card.subtitle}</p>
                    {(card as any).details && (
                      <p className="text-xs text-gray-400 mb-1 truncate" title={(card as any).details}>
                        {(card as any).details}
                      </p>
                    )}
                    {(card as any).extraDetails && (
                      <p className="text-xs text-gray-400 mb-1 truncate" title={(card as any).extraDetails}>
                        {(card as any).extraDetails}
                      </p>
                    )}
                    {card.percentage > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${card.color}`}
                          style={{ width: `${Math.min(card.percentage, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`h-1 ${card.color} transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left`}></div>
              </div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Pie Chart - Vehicle Status */}
          {(hasPermission("vehicles", "read") || user?.isSuperAdmin) && (
            vehicleStatusData.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-6">
                <Car className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  État des véhicules
                </h2>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={vehicleStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {vehicleStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex items-center justify-center h-[250px]">
              <div className="text-center">
                <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun véhicule enregistré</p>
              </div>
            </div>
          ))}

          {/* Pie Chart - Vehicle Types */}
          {(hasPermission("vehicles", "read") || user?.isSuperAdmin) && (
            vehicleTypeChartData.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-6">
                <Car className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Types de véhicules
                </h2>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={vehicleTypeChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {vehicleTypeChartData.map((_, index) => (
                      <Cell key={`cell-type-${index}`} fill={[
                        '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
                        '#EC4899', '#F43F5E', '#F97316', '#EAB308',
                        '#84CC16', '#22C55E', '#14B8A6', '#06B6D4', '#0EA5E9'
                      ][index % 13]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex items-center justify-center h-[250px]">
              <div className="text-center">
                <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun type de véhicule</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Activity Overview */}
          {(hasPermission("damages", "read") || hasPermission("drivers", "read") || user?.isSuperAdmin) && (
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-6">
                <Activity className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Aperçu de l'activité
                </h2>
              </div>
              
              <div className="space-y-6">
                {/* Répartition des permis - EN PREMIER */}
                {(hasPermission("drivers", "read") || user?.isSuperAdmin) && stats?.drivers.byLicenseType && stats.drivers.byLicenseType.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Award className="w-4 h-4 text-blue-600" />
                      Répartition des permis
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {stats.drivers.byLicenseType.map((license, index) => (
                        <div
                          key={license._id}
                          className="flex items-center justify-between p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                        >
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Permis {license._id}</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {license.count}
                            </p>
                          </div>
                          <Award className="w-8 h-8 text-blue-400 opacity-50" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meilleurs conducteurs - Remplace "Sinistres par type" */}
                {(hasPermission("drivers", "read") || user?.isSuperAdmin) && stats?.damages.byDriver && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Meilleurs conducteurs
                    </h3>
                    {stats.damages.byDriver.length > 0 ? (
                      <div className="space-y-2">
                        {[...stats.damages.byDriver]
                          .sort((a, b) => (a.totalDamages || a.count || 0) - (b.totalDamages || b.count || 0))
                          .slice(0, 5)
                          .map((driver, index) => (
                            <div
                              key={driver._id}
                              onClick={() => navigate(`/chauffeurs/historique/${driver._id}`)}
                              className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100 cursor-pointer hover:bg-green-100 hover:border-green-200 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                  index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-green-500'
                                } text-white font-bold text-sm`}>
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 hover:text-blue-600">
                                    {driver.driverName || driver._id}
                                  </span>
                                  <p className="text-xs text-gray-500">ID: {driver._id}</p>
                                </div>
                              </div>
                              <span className={`text-lg font-bold ${
                                (driver.totalDamages || driver.count || 0) === 0 ? 'text-green-600' : 'text-orange-500'
                              }`}>
                                {driver.totalDamages || driver.count || 0} sinistre{(driver.totalDamages || driver.count || 0) > 1 ? 's' : ''}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 font-medium">Excellente nouvelle !</p>
                        <p className="text-sm text-green-600">Aucun sinistre enregistré</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Chauffeurs avec le plus de sinistres - Alertes */}
                {(hasPermission("damages", "read") || user?.isSuperAdmin) && stats?.damages.byDriver && stats.damages.byDriver.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      Chauffeurs à surveiller
                    </h3>
                    <div className="space-y-2">
                      {[...stats.damages.byDriver]
                        .sort((a, b) => (b.totalDamages || b.count || 0) - (a.totalDamages || a.count || 0))
                        .filter(driver => (driver.totalDamages || driver.count || 0) > 0)
                        .slice(0, 5)
                        .map((driver, index) => (
                          <div
                            key={driver._id}
                            onClick={() => navigate(`/chauffeurs/historique/${driver._id}`)}
                            className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-100 cursor-pointer hover:bg-yellow-100 hover:border-yellow-200 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                              } text-white font-bold text-sm`}>
                                {index + 1}
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 hover:text-blue-600">
                                  {driver.driverName || driver._id}
                                </span>
                                <p className="text-xs text-gray-500">ID: {driver._id}</p>
                              </div>
                            </div>
                            <span className="text-lg font-bold text-red-600">
                              {driver.totalDamages || driver.count || 0} sinistre{(driver.totalDamages || driver.count || 0) > 1 ? 's' : ''}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions + Alerts */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-6">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Actions rapides
                </h2>
              </div>
              
              <div className="space-y-3">
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  if (action.module && !hasPermission(action.module as PermissionModule, "read") && !user?.isSuperAdmin) {
                    return null;
                  }
                  return (
                    <button
                      key={action.title}
                      onClick={() => navigate(action.link)}
                      className={`w-full flex items-center gap-3 p-4 ${action.color} text-white rounded-xl transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{action.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5" />
                <h2 className="text-lg font-semibold">
                  Métriques clés
                </h2>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 opacity-75" />
                    <span className="text-sm opacity-90">Kilométrage total</span>
                  </div>
                  <span className="font-bold text-lg">
                    {vehicleStats?.totalMileage
                      ? `${vehicleStats.totalMileage.toLocaleString()} km`
                      : "0 km"}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 opacity-75" />
                    <span className="text-sm opacity-90">Kilométrage moyen</span>
                  </div>
                  <span className="font-bold text-lg">
                    {typeof vehicleStats?.averageMileage === "number"
                      ? `${Math.round(vehicleStats.averageMileage).toLocaleString()} km`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 opacity-75" />
                    <span className="text-sm opacity-90">Moyenne par jour</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">
                      {vehicleStats?.totalMileage && stats?.vehicles?.total
                        ? `${Math.round(vehicleStats.totalMileage / Math.max(1, Math.ceil((Date.now() - new Date('2024-01-01').getTime()) / (1000 * 60 * 60 * 24)))).toLocaleString()} km`
                        : "N/A"}
                    </span>
                    <p className="text-xs opacity-75">depuis le 01/01/2024</p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 opacity-75" />
                    <span className="text-sm opacity-90">Durée moy. affectation</span>
                  </div>
                  <span className="font-bold text-lg">
                    {stats?.assignments.averageDuration
                      ? `${Math.round(stats.assignments.averageDuration)} j`
                      : "0 j"}
                  </span>
                </div>
              </div>
            </div>

            {/* Alerts */}
            {stats?.drivers.expiringLicenses && stats.drivers.expiringLicenses.length > 0 && (
              <div className="bg-yellow-50 rounded-2xl border border-yellow-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Bell className="w-5 h-5 text-yellow-600" />
                  <h2 className="text-lg font-semibold text-yellow-900">
                    Alertes
                  </h2>
                </div>
                <div className="space-y-2">
                  {stats.drivers.expiringLicenses.slice(0, 3).map((driver) => (
                    <div
                      key={driver._id}
                      className="flex items-start gap-2 p-3 bg-white rounded-lg"
                    >
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Permis expirant bientôt
                        </p>
                        <p className="text-xs text-gray-600">
                          {driver.firstName} {driver.lastName}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section Raccourcis Récents */}
        {(recentItems.vehicles.length > 0 || recentItems.drivers.length > 0) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Accès rapide - Récents</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentItems.vehicles.slice(0, 3).map((v) => (
                <button
                  key={v.plate}
                  onClick={() => navigate(`/assignments/new?plate=${v.plate}`)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm"
                >
                  <Car className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">{v.plate}</span>
                  {v.brand && <span className="text-blue-600 text-xs">({v.brand})</span>}
                </button>
              ))}
              {recentItems.drivers.slice(0, 3).map((d) => (
                <button
                  key={d.id}
                  onClick={() => navigate(`/chauffeurs/historique/${d.id}`)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-sm"
                >
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-800">{d.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          <NavCard
            title="Chauffeurs"
            description="Gérer les chauffeurs"
            icon={Users}
            color="bg-blue-500"
            link="/chauffeurs"
            module="drivers"
          />
          <NavCard
            title="Véhicules"
            description="Gérer le parc"
            icon={Car}
            color="bg-purple-500"
            link="/vehicules"
            module="vehicles"
          />
          <NavCard
            title="Affectations"
            description="Missions en cours"
            icon={ClipboardList}
            color="bg-green-500"
            link="/assignments"
            module="assignments"
          />
          <NavCard
            title="Historique"
            description="Historique flotte"
            icon={Clock}
            color="bg-orange-500"
            link="/historique-vehicules"
            module="vehicles"
          />
          <NavCard
            title="Carburant"
            description="Cartes & Conso"
            icon={Fuel}
            color="bg-cyan-500"
            link="/cartes-carburant"
            module="fuelCards"
          />
          <NavCard
            title="Sinistres"
            description="Suivi incidents"
            icon={AlertTriangle}
            color="bg-red-500"
            link="/amendes-degats"
            module="damages"
          />
          <NavCard
            title="Garage"
            description="Maintenance"
            icon={Wrench}
            color="bg-amber-600"
            link="/garage"
            module="vehicles"
          />
          {user?.isSuperAdmin && (
            <>
              <NavCard
                title="Rôles"
                description="Permissions"
                icon={Shield}
                color="bg-slate-700"
                link="/super-admin/roles"
              />
              <NavCard
                title="Utilisateurs"
                description="Comptes"
                icon={Shield}
                color="bg-neutral-700"
                link="/super-admin/users"
              />
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* FAB - Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Menu FAB ouvert */}
        {fabOpen && (
          <div className="absolute bottom-16 right-0 mb-2 flex flex-col gap-2 items-end animate-in fade-in slide-in-from-bottom-4 duration-200">
            <button
              onClick={() => { navigate('/assignments?quickscan=true'); setFabOpen(false); }}
              className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full shadow-lg hover:from-green-600 hover:to-emerald-700 transition-all whitespace-nowrap"
            >
              <ScanLine className="w-5 h-5" />
              <span className="font-medium">⚡ Scan Plaque</span>
            </button>
            <button
              onClick={() => { navigate('/assignments/new'); setFabOpen(false); }}
              className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all whitespace-nowrap"
            >
              <Clipboard className="w-5 h-5" />
              <span className="font-medium">Nouvelle affectation</span>
            </button>
            <button
              onClick={() => { navigate('/assignments'); setFabOpen(false); }}
              className="flex items-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-full shadow-lg hover:bg-orange-600 transition-all whitespace-nowrap"
            >
              <RotateCcw className="w-5 h-5" />
              <span className="font-medium">Gérer les retours</span>
            </button>
            <button
              onClick={() => { navigate('/vehicules'); setFabOpen(false); }}
              className="flex items-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all whitespace-nowrap"
            >
              <Car className="w-5 h-5" />
              <span className="font-medium">Voir les véhicules</span>
            </button>
          </div>
        )}
        
        {/* Bouton FAB principal */}
        <button
          onClick={() => setFabOpen(!fabOpen)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
            fabOpen 
              ? 'bg-gray-800 rotate-45' 
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
          }`}
        >
          <Plus className="w-7 h-7 text-white" />
        </button>
      </div>

      {/* Overlay pour fermer le FAB */}
      {fabOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/20" 
          onClick={() => setFabOpen(false)}
        />
      )}
    </div>
  );
};

// NavCard component
interface NavCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  link: string;
  module?: PermissionModule;
}

const NavCard = ({ title, description, icon: Icon, color, link, module }: NavCardProps) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (module && !hasPermission(module, "read")) {
    return null;
  }

  return (
    <div
      onClick={() => navigate(link)}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
      <div className="relative">
        <div className={`inline-flex p-2 sm:p-3 rounded-lg sm:rounded-xl ${color} bg-opacity-10 mb-2 sm:mb-4 group-hover:scale-110 transition-transform`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${color.replace('bg-', 'text-')}`} />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">{title}</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">{description}</p>
        <div className="flex items-center text-xs sm:text-sm font-medium text-blue-600 group-hover:text-blue-700">
          <span>Accéder</span>
          <TrendingUpIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
